<template>
  <div class="markdown-container" :class="{ 'image-layout-single': imageLayout === 'single' }" @click="handleClick">
    <!-- 如果没有文本且正在流式传输，显示 loading -->
    <div v-if="!text && isStreaming" class="markdown-loading">
      <span class="loading-dot"></span>
      <span class="loading-dot"></span>
      <span class="loading-dot"></span>
    </div>
    <!-- 统一使用节点数组渲染 -->
    <template v-else-if="text">
      <template v-for="(node, index) in renderNodes">
        <!-- 图片组件 -->
        <MarkdownImage
          v-if="node.type === 'image'"
          :key="node.key || `image-${index}`"
          :url="node.url"
          :alt="node.alt"
          :image-layout="imageLayout"
        />
        <!-- 文本/HTML 内容 -->
        <span
          v-else-if="node.type === 'text'"
          :key="node.key || `text-${index}`"
          v-html="node.html"
          class="markdown-text-node"
        ></span>
        <!-- 其他类型的节点可以在这里扩展 -->
      </template>
      <!-- 打字机光标 -->
      <span v-if="isStreaming" class="typewriter-cursor">|</span>
    </template>
  </div>
</template>

<script>
import MarkdownImage from "./MarkdownImage.vue";

export default {
  name: "MarkdownText",
  components: {
    MarkdownImage,
  },
  props: {
    text: {
      type: String,
      default: "",
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
    // 图片布局：'grid' 一行三张、1:1 宽高比（默认）；'single' 一行一张、不固定宽高比（相关问题回复）
    imageLayout: {
      type: String,
      default: "grid",
      validator: (v) => ["grid", "single"].includes(v),
    },
  },
  computed: {
    /**
     * 将 Markdown 文本解析为节点数组
     * 每个节点包含类型和数据，便于统一处理和扩展
     */
    renderNodes() {
      if (!this.text) {
        return [];
      }

      // 此判断是为了 "思考中..." 和 "抱歉，暂未学习过相关知识" 这两种情况直接渲染
      if (this.isSimpleText(this.text)) {
        return [
          {
            type: "text",
            html: `<div>${this.text}</div>`,
            key: "simple-text",
          },
        ];
      }

      // 提取所有图片信息（用于后续替换为占位符）
      const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
      const images = [];
      let match;
      let imageIndex = 0;

      while ((match = imageRegex.exec(this.text)) !== null) {
        images.push({
          alt: match[1].trim(),
          url: match[2].trim(),
          placeholder: `__IMAGE_PLACEHOLDER_${imageIndex}__`,
        });
        imageIndex++;
      }

      // 将图片替换为占位符
      let processedText = this.text;
      images.forEach((img) => {
        processedText = processedText.replace(
          `![${img.alt}](${img.url})`,
          img.placeholder
        );
      });

      // 渲染处理后的文本（包含占位符）
      const html = this.renderMarkdown(processedText);

      // 将 HTML 按占位符分割，构建节点数组
      const nodes = [];
      let htmlParts = html;
      let nodeIndex = 0;

      images.forEach((img) => {
        const parts = htmlParts.split(img.placeholder);
        if (parts.length > 1) {
          // 添加占位符之前的 HTML 文本节点
          if (parts[0]) {
            const beforeHtml = this.cleanEmptyPTags(parts[0], 'end');
            if (beforeHtml) {
              nodes.push({
                type: "text",
                html: beforeHtml,
                key: `text-${nodeIndex++}`,
              });
            }
          }
          // 添加图片节点
          nodes.push({
            type: "image",
            url: img.url,
            alt: img.alt,
            key: `image-${img.url}`, // 使用 URL 作为 key，确保 Vue 能正确复用
          });
          // 更新 htmlParts 为剩余部分
          htmlParts = parts.slice(1).join(img.placeholder);
        }
      });

      // 添加最后剩余的 HTML 文本节点
      if (htmlParts) {
        const remainingHtml = this.cleanEmptyPTags(htmlParts, 'start');
        if (remainingHtml) {
          nodes.push({
            type: "text",
            html: remainingHtml,
            key: `text-${nodeIndex++}`,
          });
        }
      }

      // 如果没有图片，直接返回一个文本节点
      if (nodes.length === 0 && html) {
        nodes.push({
          type: "text",
          html: html,
          key: "text-0",
        });
      }

      return nodes;
    },
  },
  methods: {
    /**
     * 判断是否为简单文本（不需要 Markdown 解析）
     * 用于“思考中...”“抱歉，暂未学习过相关知识”等纯中文短句的直出
     */
    isSimpleText(text) {
      const reg = /^[\u4e00-\u9fa5.，]+$/;
      return reg.test(text || "");
    },

    /**
     * 清理 HTML 字符串首尾可能出现的空 <p> 标签
     * @param {string} html
     * @param {'start'|'end'|'both'} position
     */
    cleanEmptyPTags(html, position = "both") {
      if (!html) return "";
      let cleaned = html.trim();

      if (position === "start" || position === "both") {
        cleaned = cleaned.replace(/^<p[^>]*>\s*<\/p>\s*/gi, "");
      }
      if (position === "end" || position === "both") {
        cleaned = cleaned.replace(/<p[^>]*>\s*<\/p>\s*$/gi, "");
      }

      return cleaned.trim();
    },

    // 渲染Markdown格式
    renderMarkdown(text) {
      // 此判断是为了 “思考中...” 和 “抱歉，暂未学习过相关知识” 这两种情况直接渲染不用走markdown解析逻辑
      if (this.isSimpleText(text)) {
        return `<div>${text}</div>`;
      }
      const lines = text.split(/\r?\n/).filter((line) => line !== null);
      const htmlParts = [];
      let i = 0;

      while (i < lines.length) {
        const trimmedLine = lines[i].trim();

        // 空行处理
        if (!trimmedLine) {
          i++;
          continue;
        }

        // 代码块处理
        if (trimmedLine.startsWith("```")) {
          const codeBlockResult = this.parseCodeBlock(lines, i);
          htmlParts.push(codeBlockResult.html);
          i = codeBlockResult.endIndex;
          continue;
        }

        // 表格处理
        if (
          trimmedLine.includes("|") &&
          i + 1 < lines.length &&
          lines[i + 1].includes("|")
        ) {
          const tableResult = this.parseTable(lines, i);
          if (tableResult.html) {
            htmlParts.push(tableResult.html);
            i = tableResult.endIndex;
            continue;
          }
        }

        // 引用处理
        if (trimmedLine.startsWith(">")) {
          const quoteResult = this.parseBlockquote(lines, i);
          htmlParts.push(quoteResult.html);
          i = quoteResult.endIndex;
          continue;
        }

        // 分割线
        if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(trimmedLine)) {
          htmlParts.push('<hr class="markdown-hr" />');
          i++;
          continue;
        }

        // 标题处理 (支持1-6级)
        const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
        if (headerMatch) {
          const [, headerIndicator, content] = headerMatch;
          const level = headerIndicator.length;
          const levelClass = `markdown-h${level}`;
          htmlParts.push(
            `<h${level} class="${levelClass}">${this.renderInlineMarkdown(
              content
            )}</h${level}>`
          );
          i++;
          continue;
        }

        // 无序列表
        if (/^[-*+]\s/.test(trimmedLine)) {
          const listResult = this.parseUnorderedList(lines, i);
          htmlParts.push(listResult.html);
          i = listResult.endIndex;
          continue;
        }

        // 有序列表
        if (/^\d+\.\s/.test(trimmedLine)) {
          const listResult = this.parseOrderedList(lines, i);
          htmlParts.push(listResult.html);
          i = listResult.endIndex;
          continue;
        }

        // 图片占位符处理 - 不要包裹在 <p> 标签中
        if (/^__IMAGE_PLACEHOLDER_\d+__$/.test(trimmedLine)) {
          htmlParts.push(trimmedLine);
          i++;
          continue;
        }

        // 行内代码和普通段落
        htmlParts.push(
          `<p class="markdown-p">${this.renderInlineMarkdown(trimmedLine)}</p>`
        );
        i++;
      }

      return htmlParts.join("");
    },

    // 解析代码块
    parseCodeBlock(lines, startIndex) {
      let i = startIndex + 1;
      const codeLines = [];
      const firstLine = lines[startIndex].trim();
      const language = firstLine.substring(3).trim();

      // 收集代码内容
      while (i < lines.length) {
        const line = lines[i];
        if (line.trim() === "```") {
          i++; // 跳过结束标记
          break;
        }
        codeLines.push(line);
        i++;
      }

      let html = "";
      if (codeLines.length > 0) {
        const codeContent = codeLines
          .map((line) => this.escapeHtml(line || "\n"))
          .join("");
        const languageHtml = language
          ? `<div class="markdown-code-language">${this.escapeHtml(
              language
            )}</div>`
          : "";
        html = `<div class="markdown-code-block">${languageHtml}<div class="markdown-code-content"><pre><code>${codeContent}</code></pre></div></div>`;
      }

      return {
        html,
        endIndex: i,
      };
    },

    // 解析表格
    parseTable(lines, startIndex) {
      const tableLines = [];
      let currentIndex = startIndex;

      // 收集表格行
      while (currentIndex < lines.length) {
        const line = lines[currentIndex].trim();
        if (line.includes("|")) {
          tableLines.push(line);
          currentIndex++;
        } else if (line === "") {
          currentIndex++;
          continue;
        } else {
          break;
        }
      }

      if (tableLines.length < 2) {
        return { html: "", endIndex: startIndex };
      }

      // 解析表头
      const headerCells = this.parseTableRow(tableLines[0]);
      if (headerCells.length === 0) {
        return { html: "", endIndex: startIndex };
      }

      let html = '<div class="markdown-table">';
      html += '<div class="markdown-table-header">';
      headerCells.forEach((cell) => {
        html += `<div class="markdown-table-cell markdown-table-header-cell"><span class="markdown-table-text">${this.renderInlineMarkdown(
          cell
        )}</span></div>`;
      });
      html += "</div>";

      // 跳过分隔行，解析数据行
      tableLines.slice(2).forEach((row) => {
        const cells = this.parseTableRow(row);
        if (cells.length > 0) {
          html += '<div class="markdown-table-row">';
          cells.forEach((cell) => {
            html += `<div class="markdown-table-cell"><span class="markdown-table-text">${this.renderInlineMarkdown(
              cell
            )}</span></div>`;
          });
          html += "</div>";
        }
      });

      html += "</div>";

      return {
        html,
        endIndex: currentIndex,
      };
    },

    // 解析表格行
    parseTableRow(row) {
      return row
        .split("|")
        .slice(1, -1) // 去掉首尾空元素
        .map((cell) => cell.trim())
        .filter((cell) => cell !== "");
    },

    // 解析引用块
    parseBlockquote(lines, startIndex) {
      const quoteLines = [];
      let i = startIndex;

      // 收集引用内容
      while (i < lines.length) {
        const line = lines[i].trim();
        if (line.startsWith(">")) {
          quoteLines.push(line.substring(1).trim());
          i++;
        } else if (line === "") {
          quoteLines.push("");
          i++;
        } else {
          break;
        }
      }

      if (quoteLines.length === 0) {
        return { html: "", endIndex: startIndex };
      }

      const content = quoteLines.join("\n").trim();
      const innerHtml = this.renderInlineMarkdown(content);
      const html = `<div class="markdown-blockquote"><div class="markdown-blockquote-border"></div><div class="markdown-blockquote-content"><span class="markdown-blockquote-text">${innerHtml}</span></div></div>`;

      return {
        html,
        endIndex: i,
      };
    },

    // 渲染行内Markdown元素（粗体、斜体、行内代码、链接等）
    // 注意：图片已在 renderNodes 中被提取并替换为占位符，此处不再处理图片
    renderInlineMarkdown(text) {
      if (!text) {
        return "";
      }

      // 转义 HTML
      let html = this.escapeHtml(text);

      // 粗体 **text**
      html = html.replace(
        /\*\*([^*]+)\*\*/g,
        '<strong class="markdown-bold">$1</strong>'
      );

      // 斜体 *text* (需要避免与粗体冲突)
      html = html.replace(
        /(?<!\*)\*([^*]+?)\*(?!\*)/g,
        '<em class="markdown-italic">$1</em>'
      );

      // 行内代码 `code`
      html = html.replace(
        /`([^`]+)`/g,
        '<code class="markdown-inline-code">$1</code>'
      );

      // 链接 [text](url)
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        const escapedUrl = this.escapeHtml(url);
        const escapedText = this.escapeHtml(text);
        return `<a href="${escapedUrl}" class="markdown-link" data-url="${escapedUrl}" data-text="${escapedText}">${escapedText}</a>`;
      });

      return html;
    },

    // 解析无序列表
    parseUnorderedList(lines, startIndex) {
      const listItems = [];
      let i = startIndex;

      while (i < lines.length) {
        const line = lines[i].trim();
        if (/^[-*+]\s/.test(line)) {
          const content = line.replace(/^[-*+]\s/, "");
          listItems.push(content);
          i++;
        } else if (line === "") {
          i++;
          continue;
        } else {
          break;
        }
      }

      if (listItems.length === 0) {
        return { html: "", endIndex: startIndex };
      }

      const listHtml = `<div class="markdown-list"><div class="markdown-ul">${listItems
        .map((item) => {
          return `<div class="markdown-li"><span class="markdown-li-bullet">• </span><span class="markdown-li-content">${this.renderInlineMarkdown(
            item
          )}</span></div>`;
        })
        .join("")}</div></div>`;

      return {
        html: listHtml,
        endIndex: i,
      };
    },

    // 解析有序列表
    parseOrderedList(lines, startIndex) {
      const listItems = [];
      let i = startIndex;

      while (i < lines.length) {
        const line = lines[i].trim();
        const match = line.match(/^(\d+)\.\s(.*)$/);
        if (match) {
          const [, number, content] = match;
          listItems.push({ number, content });
          i++;
        } else if (line === "") {
          i++;
          continue;
        } else {
          break;
        }
      }

      if (listItems.length === 0) {
        return { html: "", endIndex: startIndex };
      }

      const listHtml = `<div class="markdown-list"><div class="markdown-ol">${listItems
        .map((item) => {
          return `<div class="markdown-li"><span class="markdown-li-number">${
            item.number
          }. </span><span class="markdown-li-content">${this.renderInlineMarkdown(
            item.content
          )}</span></div>`;
        })
        .join("")}</div></div>`;

      return {
        html: listHtml,
        endIndex: i,
      };
    },

    // 处理容器点击事件（事件委托）
    handleClick(event) {
      const target = event.target;
      if (target.classList.contains("markdown-link")) {
        event.preventDefault();
        const url = target.getAttribute("data-url");
        const text = target.getAttribute("data-text");
        this.onLinkClick(text, url);
      }
    },

    // 处理链接点击事件
    onLinkClick(text, url) {
      // 可以在这里处理链接点击，比如打开新窗口
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },

    // HTML转义
    escapeHtml(text) {
      if (!text) return "";
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
  },
};
</script>

<style lang="scss" scoped>
.markdown-container {
  width: 100%;
  line-height: 1.6;
  color: #333;
  // 确保内容区域宽度固定，长网址自动换行
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;

  // 标题样式
  :deep(.markdown-h1) {
    font-size: 2em;
    font-weight: bold;
    margin: 0.67em 0;
    line-height: 1.2;
  }

  :deep(.markdown-h2) {
    font-size: 1.5em;
    font-weight: bold;
    margin: 0.75em 0;
    line-height: 1.3;
  }

  :deep(.markdown-h3) {
    font-size: 1.17em;
    font-weight: bold;
    margin: 0.83em 0;
    line-height: 1.4;
  }

  :deep(.markdown-h4) {
    font-size: 1em;
    font-weight: bold;
    margin: 1em 0;
  }

  :deep(.markdown-h5) {
    font-size: 0.83em;
    font-weight: bold;
    margin: 1.17em 0;
  }

  :deep(.markdown-h6) {
    font-size: 0.67em;
    font-weight: bold;
    margin: 1.33em 0;
  }

  // 段落样式
  :deep(.markdown-p) {
    margin: 1em 0;
    // 移除图片之间的空白间隙（只影响包含图片的段落）
    word-spacing: -0.25em; // 减少空白间隙
    // 确保长网址自动换行
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  // 代码块样式
  :deep(.markdown-code-block) {
    background-color: #f4f4f4;
    border-radius: 4px;
    padding: 1em;
    margin: 1em 0;
    overflow-x: auto;

    .markdown-code-language {
      font-size: 0.85em;
      color: #666;
      margin-bottom: 0.5em;
      font-weight: bold;
    }

    .markdown-code-content {
      pre {
        margin: 0;
        padding: 0;
        background: transparent;
        font-family: "Courier New", "Monaco", "Consolas", monospace;
        font-size: 0.9em;
        line-height: 1.5;
        white-space: pre-wrap;
        word-wrap: break-word;

        code {
          display: block;
          color: #333;
        }
      }
    }
  }

  // 行内代码样式
  :deep(.markdown-inline-code) {
    background-color: #f4f4f4;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: "Courier New", "Monaco", "Consolas", monospace;
    font-size: 0.9em;
    color: #e83e8c;
  }

  // 表格样式
  :deep(.markdown-table) {
    width: 100%;
    margin: 1em 0;
    border-collapse: collapse;
    display: flex;
    flex-direction: column;

    .markdown-table-header {
      display: flex;
      background-color: #f4f4f4;
      font-weight: bold;
      border-bottom: 2px solid #ddd;
    }

    .markdown-table-row {
      display: flex;
      border-bottom: 1px solid #ddd;

      &:nth-child(even) {
        background-color: #f9f9f9;
      }
    }

    .markdown-table-cell {
      flex: 1;
      padding: 0.5em 1em;
      border-right: 1px solid #ddd;

      &:last-child {
        border-right: none;
      }

      .markdown-table-text {
        display: block;
      }
    }

    .markdown-table-header-cell {
      background-color: #f4f4f4;
    }
  }

  // 引用样式
  :deep(.markdown-blockquote) {
    display: flex;
    margin: 1em 0;
    border-left: 4px solid #ddd;
    padding-left: 1em;
    color: #666;
    font-style: italic;

    .markdown-blockquote-border {
      width: 4px;
      background-color: #ddd;
      margin-right: 1em;
    }

    .markdown-blockquote-content {
      flex: 1;

      .markdown-blockquote-text {
        display: block;
      }
    }
  }

  // 列表样式
  :deep(.markdown-list) {
    margin: 1em 0;

    .markdown-ul,
    .markdown-ol {
      padding-left: 0;
    }

    .markdown-li {
      display: flex;
      margin: 0.5em 0;
      align-items: flex-start;

      .markdown-li-bullet,
      .markdown-li-number {
        margin-right: 0.5em;
        font-weight: bold;
      }

      .markdown-li-content {
        flex: 1;
      }
    }
  }

  // 分割线样式
  :deep(.markdown-hr) {
    border: none;
    border-top: 1px solid #ddd;
    margin: 1em 0;
  }

  // 粗体样式
  :deep(.markdown-bold) {
    font-weight: bold;
  }

  // 斜体样式
  :deep(.markdown-italic) {
    font-style: italic;
  }

  // 链接样式
  :deep(.markdown-link) {
    color: #0066cc;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  // 文本节点样式 - 确保不会影响图片排列
  .markdown-text-node {
    display: block; // 文本节点是块级，确保图片前后的文本正常换行
  }
  
  // 图片样式 - 默认一行展示三张，每张占1/3宽度，宽高比1:1
  :deep(.markdown-image) {
    width: calc(33.333% - 4px); // 每张图片占1/3宽度，减去间距
    aspect-ratio: 1 / 1; // 宽高比1:1
    object-fit: cover; // 保持宽高比，裁剪多余部分
    display: inline-block;
    vertical-align: top;
    margin: 2px; // 图片之间的间距
    box-sizing: border-box;
    border-radius: 4px;
  }

  // 相关问题回复：一行一个、不固定宽高比（放在后面以覆盖上面的默认图片样式）
  &.image-layout-single :deep(.markdown-image) {
    width: 100%;
    aspect-ratio: auto;
    object-fit: contain;
    display: block;
    margin: 8px 0;
  }

  // 打字机光标样式
  :deep(.typewriter-cursor) {
    display: inline-block;
    color: #666666;
    font-weight: bold;
    animation: blink 1s infinite;
    margin-left: 2px;
    vertical-align: baseline;
  }

  // Loading 加载样式
  :deep(.markdown-loading) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 0;
    gap: 6px;
  }

  :deep(.loading-dot) {
    width: 8px;
    height: 8px;
    background-color: #666666;
    border-radius: 50%;
    animation: dotFlashing 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

@keyframes dotFlashing {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
</style>
